import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FlaskConical, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        
        // In MOCK mode, show the reset link
        if (data.mock_reset_link) {
          setResetLink(data.mock_reset_link);
          toast.info('DEVELOPMENT MODE: Reset link generated', {
            duration: 10000
          });
        } else {
          toast.success('Check your email for reset instructions');
        }
      } else {
        toast.error(data.message || 'Failed to process request');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" className="flex justify-center mb-4">
            <FlaskConical className="h-12 w-12 text-blue-600" />
          </Link>
          <CardTitle className="text-2xl">
            {submitted ? 'Check Your Email' : 'Forgot Password?'}
          </CardTitle>
          <p className="text-sm text-slate-600 mt-2">
            {submitted
              ? "We've sent you a password reset link"
              : 'Enter your email to receive a password reset link'}
          </p>
        </CardHeader>

        <CardContent>
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@university.edu"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <div className="text-center">
                <Link to="/author/login" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <p className="font-medium">Reset link sent!</p>
                  <p className="mt-1">
                    Check your email inbox for a password reset link. 
                    The link will expire in 1 hour.
                  </p>
                </div>
              </div>

              {resetLink && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-yellow-800 mb-2">
                    🔧 DEVELOPMENT MODE
                  </p>
                  <p className="text-xs text-yellow-700 mb-2">
                    In production, this link would be sent via email:
                  </p>
                  <a
                    href={resetLink}
                    className="text-xs text-blue-600 hover:underline break-all"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {resetLink}
                  </a>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                  setResetLink('');
                }}
              >
                Send Another Link
              </Button>

              <div className="text-center">
                <Link to="/author/login" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
